import { useMemo } from 'react'
import { BUILD_LOG, SURFACE_LABEL } from './buildLog'
import type { BuildLogEntry, Surface } from './buildLog'
import type { ArcadeWindow, BoardModel } from './viewModel'

/**
 * "What's new" — the build log, for everyone rather than for administrators.
 *
 * It reads a committed list and nothing else. No fetch, no endpoint, no
 * dependency on the payload, which is why it renders on a window where every
 * other view is empty: the platform's history does not stop existing because
 * nobody used it in the last four hours.
 *
 * THE WINDOW PILL IS HONOURED, because a control that some views obey and
 * others silently ignore teaches people not to trust it. On a short window
 * this says so in words rather than showing an empty list, which is the same
 * decision `WindowTooShort` makes for the charts.
 */

/** How many days back each window reaches, for filtering entries. Null is ALL.
 *  The sub-day windows all collapse to "today", because an entry is dated to a
 *  day: there is no such thing as a release that happened four hours ago and
 *  not today. */
const WINDOW_DAYS: Record<ArcadeWindow, number | null> = {
  '1h': 1,
  '4h': 1,
  '8h': 1,
  '12h': 1,
  '24h': 1,
  day: 1,
  wk: 7,
  mo: 30,
  all: null,
}

function cutoffFor(win: ArcadeWindow): string | null {
  const days = WINDOW_DAYS[win]
  if (days === null) return null
  const d = new Date()
  d.setUTCDate(d.getUTCDate() - (days - 1))
  return d.toISOString().slice(0, 10)
}

/** 2026-08-17 -> 17 Aug 2026. Written out rather than pulled from Intl so the
 *  string is identical in a test and in a browser with a different locale. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export function formatDay(iso: string): string {
  const [y, m, d] = iso.split('-')
  const month = MONTHS[Number(m) - 1] ?? m
  return `${Number(d)} ${month} ${y}`
}
export function formatMonth(iso: string): string {
  const [y, m] = iso.split('-')
  const month = MONTHS[Number(m) - 1] ?? m
  return `${month} ${y}`
}

function Row({ entry }: { entry: BuildLogEntry }) {
  return (
    <div className="blrow">
      <div className="bldate">{formatDay(entry.date)}</div>
      <div className="blbody">
        <div className="bltitle">
          {entry.title}
          <span className={`blchip bl-${entry.surface}`}>{SURFACE_LABEL[entry.surface]}</span>
          {entry.kind === 'improvement' ? <span className="blkind">improved</span> : null}
        </div>
        <div className="bldetail">{entry.detail}</div>
      </div>
    </div>
  )
}

export function BuildLogView({ model, tab }: { model: BoardModel; tab: string }) {
  const win = model.window
  const cutoff = cutoffFor(win)

  const inWindow = useMemo(
    () => (cutoff ? BUILD_LOG.filter((e) => e.date >= cutoff) : [...BUILD_LOG]),
    [cutoff],
  )

  // Counted over the WHOLE log, never the window: "days live" and "changes
  // shipped" are facts about the platform, and a KPI that shrank when you
  // changed a filter would be answering a different question from the one its
  // label asks.
  const first = BUILD_LOG[BUILD_LOG.length - 1]
  const latest = BUILD_LOG[0]
  const daysLive = useMemo(() => {
    if (!first) return 0
    const start = Date.parse(`${first.date}T00:00:00Z`)
    return Math.max(1, Math.round((Date.now() - start) / 86_400_000))
  }, [first])

  if (tab === 'method') {
    return (
      <div className="method">
        <div className="card">
          <div className="cardhead">
            <div>
              <div className="ctitle">Where this comes from</div>
              <div className="csub">How the list is made, and what it will not tell you</div>
            </div>
          </div>
          <table>
            <tbody>
              <tr>
                <td>What this is</td>
                <td>
                  Changes to CHU GPT that you can actually see or use, written in plain words rather
                  than taken from the engineering history.
                </td>
              </tr>
              <tr>
                <td>Where it comes from</td>
                <td>
                  A person writes each entry when something ships. The engineering history proposes
                  candidates; it never writes this page.
                </td>
              </tr>
              <tr>
                <td>When it updates</td>
                <td>
                  At each release. A test fails the build if the list falls behind what is deployed,
                  so it cannot quietly go stale.
                </td>
              </tr>
              <tr>
                <td>Grain</td>
                <td>One entry per user-visible change, dated to the day it went live.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="card">
          <div className="cardhead">
            <div>
              <div className="ctitle">What it cannot tell you</div>
            </div>
          </div>
          <ul>
            <li>
              <b>It is not everything that happened.</b> Most engineering work is invisible from the
              outside — making things faster, safer or cheaper — and none of that appears here.
            </li>
            <li>
              <b>It is not a changelog.</b> There is no version number against an entry, because the
              thing that matters is what you can do, not which build it arrived in.
            </li>
            <li>
              <b>Absence is not evidence.</b> If something is missing it may simply not have been
              written up yet. Ask, rather than concluding it does not exist.
            </li>
            <li>
              <b>It says nothing about who can use what.</b> Some tools are limited to a team.
              Seeing an entry here does not mean it is switched on for you.
            </li>
          </ul>
        </div>
      </div>
    )
  }

  if (tab === 'timeline') {
    // Grouped by month, newest first. Months come from the entries rather than
    // from a range, so an empty month cannot appear as a heading with nothing
    // beneath it.
    const months: Array<[string, BuildLogEntry[]]> = []
    for (const e of BUILD_LOG) {
      const key = e.date.slice(0, 7)
      const last = months[months.length - 1]
      if (last && last[0] === key) last[1].push(e)
      else months.push([key, [e]])
    }
    return (
      <>
        {months.map(([key, entries]) => (
          <div className="card" key={key}>
            <div className="cardhead">
              <div>
                <div className="ctitle">{formatMonth(`${key}-01`)}</div>
                <div className="csub">
                  {entries.length} {entries.length === 1 ? 'change' : 'changes'}
                </div>
              </div>
            </div>
            <div className="bllist">
              {entries.map((e) => (
                <Row entry={e} key={`${e.date}-${e.title}`} />
              ))}
            </div>
          </div>
        ))}
      </>
    )
  }

  return (
    <>
      <div className="kpis">
        <div className="kpi">
          <div className="k">Changes shipped</div>
          <div className="v">{BUILD_LOG.length}</div>
          <div className="d">user-visible, since launch</div>
        </div>
        <div className="kpi">
          <div className="k">Days live</div>
          <div className="v">{daysLive}</div>
          <div className="d">since {first ? formatDay(first.date) : '—'}</div>
        </div>
        <div className="kpi">
          <div className="k">Most recent</div>
          <div className="v">{latest ? formatDay(latest.date) : '—'}</div>
          <div className="d">{latest ? latest.title : '—'}</div>
        </div>
      </div>

      <div className="card">
        <div className="cardhead">
          <div>
            <div className="ctitle">What&rsquo;s new</div>
            <div className="csub">
              {cutoff ? `Shipped in the ${win.toUpperCase()} window` : 'Everything, newest first'}
            </div>
          </div>
        </div>
        {inWindow.length === 0 ? (
          <p className="hint">
            NOTHING SHIPPED IN A {win.toUpperCase()} WINDOW. The platform has changed{' '}
            {BUILD_LOG.length} times since {first ? formatDay(first.date) : 'launch'} — it is the
            WINDOW that is empty, not the history. Pick MO or ALL to see it, or open THE WHOLE BUILD
            for everything at once.
          </p>
        ) : (
          <div className="bllist">
            {inWindow.map((e) => (
              <Row entry={e} key={`${e.date}-${e.title}`} />
            ))}
          </div>
        )}
        <div className="cnote">
          Written by hand when something ships, not generated from the engineering history. See HOW
          THIS LIST IS MADE for what that means and what it leaves out.
        </div>
      </div>
    </>
  )
}

/** Exported for the tests: the surfaces actually used by the log, so a new
 *  surface with no styling cannot ship unnoticed. */
export function surfacesInUse(): Surface[] {
  return [...new Set(BUILD_LOG.map((e) => e.surface))].sort()
}
